import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import useMyTrainingsData from './hooks/useMyTrainingsData'
import SearchActionsBar from '@/widgets/SearchActionsBar'
import LoadMoreSection from '@/widgets/LoadMoreSection'
import MyTrainingsTable from './components/MyTrainingsTable'
import PlacowkaFilterModal from '@/filter-modals/components/PlacowkaFilterModal'
import StatusFilterModal from '@/filter-modals/components/StatusFilterModal'

export default function MyTrainings() {
	const { t } = useTranslation()
	const [searchInput, setSearchInput] = useState('')
	const [selectedStatus, setSelectedStatus] = useState('')
	const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false)
	const [isPlacowkaFilterOpen, setIsPlacowkaFilterOpen] = useState(false)
	const [selectedPlacowkaId, setSelectedPlacowkaId] = useState(null)

	const {
		data,
		loading,
		loadingMore,
		error,
		totalCount,
		hasMore,
		loadData,
		handleLoadMore,
		setSorting,
		updateFilters,
		sortColumn,
		sortDirection,
		clearError,
	} = useMyTrainingsData()

	const statusOptions = useMemo(() => [
		{ value: 'new', label: t('trainings.list.statusOptions.new') },
		{ value: 'assigned', label: t('trainings.list.statusOptions.assigned') },
		{ value: 'completed', label: t('trainings.list.statusOptions.completed') },
		{ value: 'approved', label: t('trainings.list.statusOptions.approved') },
		{ value: 'paid', label: t('trainings.list.statusOptions.paid') },
	], [t])

	useEffect(() => {
		if (error) {
			Swal.fire({
				title: t('notifications.errorTitle'),
				text: error,
				icon: 'error',
			}).then(() => {
				clearError()
			})
		}
	}, [clearError, error, t])

	useEffect(() => {
		loadData()
	}, [loadData])

	const handleSort = column => {
		const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc'
		setSorting(column, newDirection)
	}

	const handleSearchChange = e => {
		setSearchInput(e.target.value)
	}

	const handleSearchSubmit = useCallback(() => {
		updateFilters({ searchText: searchInput.trim() })
	}, [searchInput, updateFilters])

	const handleApplyStatusFilter = useCallback(status => {
		setSelectedStatus(status || '')
		updateFilters({ status: status || null })
		setIsStatusFilterOpen(false)
	}, [updateFilters])

	const handleApplyPlacowkaFilter = useCallback(placowkaId => {
		updateFilters({ placowkaId: placowkaId || null })
		setSelectedPlacowkaId(placowkaId || null)
		setIsPlacowkaFilterOpen(false)
	}, [updateFilters])

	const buttons = [
		{
			key: 'search',
			label: t('myTrainings.list.actions.search'),
			onClick: handleSearchSubmit,
			className: 'px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition flex items-center justify-center',
		},
	]

	return (
		<>
			<SearchActionsBar
				value={searchInput}
				onChange={handleSearchChange}
				placeholder={t('myTrainings.list.searchPlaceholder')}
				buttons={buttons}
			/>

			<MyTrainingsTable
				data={data}
				loading={loading}
				sortColumn={sortColumn}
				sortDirection={sortDirection}
				onSort={handleSort}
				hasPlacowkaFilter={!!selectedPlacowkaId}
				onOpenPlacowkaFilter={() => setIsPlacowkaFilterOpen(true)}
				hasStatusFilter={!!selectedStatus}
				onOpenStatusFilter={() => setIsStatusFilterOpen(true)}
			/>

			<LoadMoreSection
				hasMore={hasMore}
				loadingMore={loadingMore}
				onLoadMore={handleLoadMore}
				dataLength={data.length}
				totalCount={totalCount}
			/>

			<StatusFilterModal
				isOpen={isStatusFilterOpen}
				onClose={() => setIsStatusFilterOpen(false)}
				selectedStatus={selectedStatus}
				onApply={handleApplyStatusFilter}
				statusOptions={statusOptions}
				title={t('trainings.list.statusFilter.title')}
				description={t('trainings.list.statusFilter.prompt')}
				applyLabel={t('trainings.list.statusFilter.apply')}
				clearLabel={t('trainings.list.statusFilter.clear')}
			/>

			<PlacowkaFilterModal
				isOpen={isPlacowkaFilterOpen}
				onClose={() => setIsPlacowkaFilterOpen(false)}
				selectedPlacowkaId={selectedPlacowkaId}
				onApply={handleApplyPlacowkaFilter}
				title={t('myTrainings.list.filters.placowkaTitle')}
				description={t('myTrainings.list.filters.placowkaDescription')}
				applyLabel={t('myTrainings.list.filters.apply')}
				clearLabel={t('myTrainings.list.filters.clear')}
			/>
		</>
	)
}
